/**
 * VariantsGuide: the visual guide of the subject variants (the Subject variant tab of a subject in the bank,
 * /questions?folder=<subject>&tab=variant), opened by the "?" after the variant list's title and after an open
 * variant's name (HelpButton id="variants": both share the seen mark). Written as BankGuide is: HelpKit blocks in
 * their order, Mini pictures of the real controls, the sentences under help.variants.* and every button named through
 * its own i18n key.
 * - The steps follow a variant's life: create it for a grade, let the system propose its questions, change them place
 *   by place, choose one in the candidates dialog, read the check (a matrix week without a topic blocks the variant
 *   until the matrix names one), confirm it, and how a BTS booklet takes and locks it.
 * - It follows the screen: under 768px (VariantDetail's header, OverflowMenu's breakpoint) the header keeps its step
 *   and "⋯" in one row, with Student's view and Propose again inside "⋯"; under 1024px (CandidatesDialog's two panes)
 *   a candidate is tapped to show its text and its button.
 * - Only a subject's moderator and admins open the tab (canBuildVariants), so the roles block has those two.
 */
import React from "react";
import { useTranslation } from "react-i18next";
import {
  ArrowPathIcon,
  ArrowUturnLeftIcon,
  ArrowsRightLeftIcon,
  CheckBadgeIcon,
  EllipsisHorizontalIcon,
  ExclamationTriangleIcon,
  EyeIcon,
  KeyIcon,
  LockClosedIcon,
  MagnifyingGlassIcon,
  PlusIcon,
  ShieldCheckIcon,
  SparklesIcon,
  TableCellsIcon,
  XCircleIcon,
  XMarkIcon,
} from "@heroicons/react/24/outline";
import { HelpFlow, HelpIntro, HelpLegend, HelpRoles, HelpStep, HelpSteps, HelpText, HelpTips } from "../HelpKit";
import {
  MiniBadge,
  MiniButton,
  MiniCard,
  MiniDialog,
  MiniDivider,
  MiniField,
  MiniLevel,
  MiniRadio,
  MiniRow,
  MiniStack,
  MiniTable,
  MiniText,
} from "../Mini";
import { useMediaQuery } from "../../../pages/Exams/TestBuilder/ui";
import { LockedBadge, VariantStatusBadge } from "../../SubjectVariants/variantUi";

// The example: grade 10 of the example subject, planned at 20 questions and 100 points (grade 11 at 12 and 58).
const GRADE = 10;
// A question's number in the variant (QuestionContent's NumberBadge): dark.
const NUMBER = "bg-slate-900 text-white ring-slate-900";

export default function VariantsGuide() {
  const [t] = useTranslation();
  // VariantDetail's header (OverflowMenu's breakpoint) and CandidatesDialog's two panes.
  const wide = useMediaQuery("(min-width: 768px)");
  const twoPanes = useMediaQuery("(min-width: 1024px)");
  const ex = (key) => t(`help.common.example.${key}`);
  const variant = t("help.variants.example.variant");
  const plan = (count, points) => t("variants.create.grade_plan", { count, pointsText: t("difficulty.points", { count: points }) });
  const week = (quarter, n) => `${t("variants.row.quarter", { quarter })}, ${t("variants.row.week", { week: n })}`;

  // The labels of the real controls.
  const create = t("variants.list.create");
  const submit = t("variants.create.submit");
  const propose = t("variants.actions.propose");
  const again = t("variants.actions.propose_again");
  const confirm = t("variants.actions.confirm");
  const unconfirm = t("variants.actions.unconfirm");
  const student = t("variants.actions.student_view");
  const settings = t("variants.actions.settings");
  const remove = t("variants.slot.remove");
  const replace = t("variants.slot.replace");
  const add = t("variants.slot.add");
  const chooseReplace = t("variants.candidates.choose_replace");
  const chooseAdd = t("variants.candidates.choose_add");
  const draft = t("variants.status.draft");
  const confirmed = t("variants.status.confirmed");
  // "⋯" as the header draws it: plain from md up, framed on phones.
  const more = (mark) => <MiniButton variant={wide ? "quiet" : "outline"} icon={EllipsisHorizontalIcon} mark={mark} />;
  const title = (status, tone) => (
    <MiniRow>
      <MiniText variant="title">{variant}</MiniText>
      <MiniBadge tone={tone}>{status}</MiniBadge>
    </MiniRow>
  );

  return (
    <>
      <HelpIntro>{t("help.variants.intro")}</HelpIntro>

      <HelpFlow pipeline="bts" current="variants" />

      <HelpSteps>
        <HelpStep
          title={t("help.variants.steps.create.title")}
          illustration={
            <MiniStack>
              <MiniRow className="justify-between gap-y-3">
                <MiniText variant="title">{t("variants.list.title")}</MiniText>
                <MiniButton variant="primary" icon={PlusIcon} mark="1">
                  {create}
                </MiniButton>
              </MiniRow>
              <MiniDialog
                title={t("variants.create.title")}
                footer={[
                  <MiniButton key="cancel" variant="outline">
                    {t("buttons.cancel")}
                  </MiniButton>,
                  <MiniButton key="submit" variant="primary" mark="3">
                    {submit}
                  </MiniButton>,
                ]}
              >
                <MiniText variant="muted">{t("variants.create.grade")}</MiniText>
                <MiniRadio checked hint={plan(20, 100)} mark={{ label: "2" }}>
                  {t("variants.grade", { grade: GRADE })}
                </MiniRadio>
                <MiniRadio hint={plan(12, 58)}>{t("variants.grade", { grade: GRADE + 1 })}</MiniRadio>
              </MiniDialog>
            </MiniStack>
          }
        >
          <HelpText i18nKey="help.variants.steps.create.text" values={{ create, submit }} />
        </HelpStep>

        <HelpStep
          title={t("help.variants.steps.propose.title")}
          illustration={
            <MiniStack>
              <MiniRow className="justify-between gap-y-3">
                {title(draft, "amber")}
                <MiniRow className="flex-nowrap">
                  <MiniButton variant="primary" icon={SparklesIcon} mark="1">
                    {propose}
                  </MiniButton>
                  {more()}
                </MiniRow>
              </MiniRow>
              {/* What the proposal could not fill (ProposalPanel): a place, how many, and the way to the bank. */}
              <MiniCard tone="amber" icon={ExclamationTriangleIcon}>
                <MiniText variant="strong">{`${t("variants.proposal.summary", { proposed: 19, planned: 20 })}${t("variants.proposal.missing")}`}</MiniText>
                <MiniRow className="gap-y-3">
                  {t("variants.proposal.shortfall", { level: "B", count: 1, place: `${week("I", 3)}, ${ex("topic")}` })}
                  <MiniButton variant="link" mark="2">
                    {t("variants.find_in_bank")}
                  </MiniButton>
                </MiniRow>
              </MiniCard>
            </MiniStack>
          }
        >
          <HelpText i18nKey="help.variants.steps.propose.text" values={{ propose, find: t("variants.find_in_bank") }} />
          <p>
            <HelpText i18nKey={wide ? "help.variants.steps.propose.text_again" : "help.variants.steps.propose.text_again_phone"} values={{ again }} />
          </p>
        </HelpStep>

        <HelpStep
          title={t("help.variants.steps.compose.title")}
          illustration={
            // A week of the composition: its topic, a place with its question and an empty one. The marked buttons
            // get a roomier stack (MiniStack), so their numbers do not cover the line above.
            <MiniCard>
              <MiniStack tight>
                <MiniText variant="muted" truncate>
                  {`${t("variants.row.week", { week: 3 })} · ${t("variants.row.hours", { count: 2 })}`}
                </MiniText>
                <MiniText variant="strong" truncate>
                  {ex("topic")}
                </MiniText>
              </MiniStack>
              <MiniStack>
                <MiniCard>
                  <MiniRow>
                    <MiniBadge className={NUMBER}>{t("variants.number", { number: 1 })}</MiniBadge>
                    <MiniLevel level="A" />
                  </MiniRow>
                  <MiniStack>
                    <MiniText truncate>{ex("question_1")}</MiniText>
                    <MiniRow className="gap-y-3">
                      <MiniButton variant="quiet" icon={ArrowsRightLeftIcon} mark="1">
                        {replace}
                      </MiniButton>
                      <MiniButton variant="quiet" icon={XMarkIcon}>
                        {remove}
                      </MiniButton>
                    </MiniRow>
                  </MiniStack>
                </MiniCard>
                <MiniCard tone="slate">
                  <MiniStack>
                    <MiniRow>
                      <MiniLevel level="B" />
                      <MiniText variant="muted">{t("variants.slot.empty")}</MiniText>
                    </MiniRow>
                    <MiniRow>
                      <MiniButton variant="link" icon={PlusIcon} mark="2">
                        {add}
                      </MiniButton>
                    </MiniRow>
                  </MiniStack>
                </MiniCard>
              </MiniStack>
            </MiniCard>
          }
        >
          <HelpText i18nKey="help.variants.steps.compose.text" values={{ replace, remove, add }} />
        </HelpStep>

        <HelpStep
          title={t("help.variants.steps.choose.title")}
          illustration={
            <MiniDialog
              title={t("variants.candidates.replace_title", { number: 1 })}
              footer={[
                <MiniButton key="cancel" variant="outline">
                  {t("buttons.cancel")}
                </MiniButton>,
                twoPanes && (
                  <MiniButton key="choose" variant="primary" mark="2">
                    {chooseReplace}
                  </MiniButton>
                ),
              ]}
            >
              <MiniField icon={MagnifyingGlassIcon} placeholder={t("variants.candidates.search_placeholder")} />
              {/* The chosen candidate; phones open its text and its button under it. */}
              <MiniCard tone="blue" mark={{ label: "1" }}>
                <MiniRow>
                  <MiniLevel level="A" />
                  <MiniText variant="strong" truncate>
                    {ex("question_2")}
                  </MiniText>
                </MiniRow>
                <MiniText variant="muted">{t("variants.candidates.unused")}</MiniText>
              </MiniCard>
              {!twoPanes && (
                <MiniCard>
                  <MiniText>{ex("question_2")}</MiniText>
                  <MiniRow className="gap-y-3">
                    <MiniButton variant="primary" mark="2">
                      {chooseReplace}
                    </MiniButton>
                  </MiniRow>
                </MiniCard>
              )}
              <MiniCard>
                <MiniRow>
                  <MiniLevel level="A" />
                  <MiniText variant="strong" truncate>
                    {ex("question_3")}
                  </MiniText>
                </MiniRow>
                <MiniText variant="muted">{t("variants.candidates.uses", { count: 2 })}</MiniText>
              </MiniCard>
            </MiniDialog>
          }
        >
          <HelpText i18nKey="help.variants.steps.choose.text" />{" "}
          <HelpText
            i18nKey={twoPanes ? "help.variants.steps.choose.pick" : "help.variants.steps.choose.pick_phone"}
            values={{ replace: chooseReplace, add: chooseAdd }}
          />
        </HelpStep>

        <HelpStep
          title={t("help.variants.steps.check.title")}
          illustration={
            <MiniStack>
              {/* Planned against actual, as the check's table; a phone's picture leaves the points out (its headers
                  would break inside a word), the text names them. */}
              <MiniTable
                columns={[
                  { label: "", width: wide ? "24%" : "30%" },
                  ...["A", "B", "C"].map((level) => ({ label: level, width: wide ? "11%" : "14%", align: "right" })),
                  { label: t("variants.check.col_count"), align: "right" },
                  wide && { label: t("variants.check.col_points"), width: "21%", align: "right" },
                ].filter(Boolean)}
                rows={[
                  [t("variants.check.planned"), "10", "6", "4", "20", "100"],
                  [t("variants.check.actual"), "10", "5", "4", "19", "95"],
                ].map((row) => (wide ? row : row.slice(0, 5)))}
              />
              <MiniText variant="caps">{t("variants.check.blocking_title")}</MiniText>
              {/* A week of the matrix without a topic: only the matrix can mend it, so its line leads there. */}
              <MiniCard tone="red" icon={XCircleIcon}>
                <MiniStack>
                  <MiniRow>{t("variants.issues.row_without_topic", { place: week("I", 3) })}</MiniRow>
                  <MiniRow className="gap-y-3">
                    <MiniButton variant="link" icon={TableCellsIcon} mark>
                      {t("variants.list.open_matrix")}
                    </MiniButton>
                  </MiniRow>
                </MiniStack>
              </MiniCard>
              <MiniText variant="caps">{t("variants.check.warnings_title")}</MiniText>
              <MiniCard tone="amber" icon={ExclamationTriangleIcon}>
                {t("variants.issues.option_count", { list: t("variants.number", { number: 7 }) })}
              </MiniCard>
            </MiniStack>
          }
        >
          <HelpText i18nKey="help.variants.steps.check.text" values={{ check: t("variants.check.title") }} />
          <p>
            <HelpText i18nKey="help.variants.steps.check.text_topic" values={{ matrix: t("variants.list.open_matrix") }} />
          </p>
        </HelpStep>

        <HelpStep
          title={t("help.variants.steps.confirm.title")}
          illustration={
            <MiniStack>
              <MiniRow className="justify-between gap-y-3">
                {title(draft, "amber")}
                {/* Wide: the everyday ones quiet beside the step; phones: the step and "⋯". */}
                <MiniRow className="flex-nowrap">
                  {wide && (
                    <MiniButton variant="quiet" icon={ArrowPathIcon}>
                      {again}
                    </MiniButton>
                  )}
                  {wide && (
                    <MiniButton variant="quiet" icon={EyeIcon}>
                      {student}
                    </MiniButton>
                  )}
                  {/* Phones: Confirm, then Student's view inside "⋯". */}
                  <MiniButton variant="primary" icon={CheckBadgeIcon} mark={wide ? true : "1"}>
                    {confirm}
                  </MiniButton>
                  {more(wide ? undefined : "2")}
                </MiniRow>
              </MiniRow>
              <MiniDivider />
              {/* Confirmed: the step turns into Back to draft (Student's view beside it from md up, Propose again in "⋯"). */}
              <MiniRow className="justify-between gap-y-3">
                {title(confirmed, "green")}
                <MiniRow className="flex-nowrap">
                  {wide && (
                    <MiniButton variant="quiet" icon={EyeIcon}>
                      {student}
                    </MiniButton>
                  )}
                  <MiniButton variant={wide ? "quiet" : "outline"} icon={ArrowUturnLeftIcon}>
                    {unconfirm}
                  </MiniButton>
                  {more()}
                </MiniRow>
              </MiniRow>
            </MiniStack>
          }
        >
          <HelpText i18nKey="help.variants.steps.confirm.text" values={{ confirm, confirmed, unconfirm }} />
          <p>
            <HelpText i18nKey={wide ? "help.variants.steps.confirm.text_student" : "help.variants.steps.confirm.text_student_phone"} values={{ student }} />
          </p>
        </HelpStep>

        <HelpStep
          title={t("help.variants.steps.booklets.title")}
          illustration={
            <MiniStack>
              <MiniRow>
                <MiniText variant="title">{variant}</MiniText>
                <MiniBadge tone="green">{confirmed}</MiniBadge>
                <MiniBadge icon={LockClosedIcon} mark={{ pointer: false }}>
                  {t("variants.locked")}
                </MiniBadge>
              </MiniRow>
              <MiniRow>
                <MiniText variant="muted">{t("variants.booklets")}:</MiniText>
                <MiniBadge tone="blue" icon={LockClosedIcon}>
                  {`${t("bts.create.suggested_name", { folder: ex("exam"), grade: GRADE, stream: t("bts.languages.kk") })} · ${t("variants.booklet_status.active")}`}
                </MiniBadge>
              </MiniRow>
              <MiniCard tone="slate" icon={LockClosedIcon}>
                <MiniText variant="strong">{t("variants.locked_banner.title")}</MiniText>
              </MiniCard>
            </MiniStack>
          }
        >
          <HelpText i18nKey="help.variants.steps.booklets.text" values={{ bts: t("nav.bts"), locked: t("variants.locked") }} />
        </HelpStep>
      </HelpSteps>

      <HelpLegend
        rows={[
          { key: "draft", badge: <VariantStatusBadge status="draft" />, text: t("help.variants.legend.draft") },
          { key: "confirmed", badge: <VariantStatusBadge status="confirmed" />, text: t("help.variants.legend.confirmed") },
          { key: "locked", badge: <LockedBadge />, text: t("help.variants.legend.locked") },
        ]}
      />

      <HelpRoles
        rows={[
          { key: "admin", role: t("folders.roles.admin"), Icon: KeyIcon, can: t("help.variants.roles.admin") },
          { key: "moderator", role: t("folders.roles.moderator"), Icon: ShieldCheckIcon, can: t("help.variants.roles.moderator") },
        ]}
      />

      <HelpTips
        items={[
          wide ? (
            <HelpText key="menu" i18nKey="help.variants.tips.menu" values={{ settings, delete: t("buttons.delete"), again }} />
          ) : (
            <HelpText key="menu" i18nKey="help.variants.tips.menu_phone" values={{ student, again, settings, delete: t("buttons.delete") }} />
          ),
          <HelpText key="printed" i18nKey="help.variants.tips.printed" values={{ settings, printed: t("variants.settings.printed") }} />,
          t("help.variants.tips.passage"),
          t("help.variants.tips.delete"),
        ]}
      />
    </>
  );
}
